"use client"

import { useEffect, useState } from "react"
import axios from "axios"
import {
  FaCheckCircle,
  FaTimesCircle,
  FaTrash,
  FaMoneyBillWave,
  FaFilter,
  FaExchangeAlt,
  FaHourglassHalf,
  FaPlusCircle,
  FaHandHoldingUsd,
} from "react-icons/fa"
import "../styles/FinanceiroAdmin.css"

const hojeLocal = () => {
  const agora = new Date()
  const local = new Date(agora.getTime() - agora.getTimezoneOffset() * 60000)
  return local.toISOString().slice(0, 10)
}

const FinanceiroCircuitoAdmin = () => {
  const [saques, setSaques] = useState([])
  const [entradas, setEntradas] = useState([])
  const [loading, setLoading] = useState(true)
  const [salvandoEntrada, setSalvandoEntrada] = useState(false)
  const [mensagem, setMensagem] = useState("")
  const [tipoMensagem, setTipoMensagem] = useState("")

  const [resumo, setResumo] = useState({
    totalDisponivel: 0,
    totalSaquesAprovados: 0,
    totalSaquesPendentes: 0,
    totalArrecadadoVendas: 0,
    totalEntradas: 0,
    totalArrecadadoGeral: 0,
  })

  const [circuitos, setCircuitos] = useState([])
  const [circuitoSelecionado, setCircuitoSelecionado] = useState("")
  const [formEntrada, setFormEntrada] = useState({
    circuitoId: "",
    tipo: "Patrocínio",
    descricao: "",
    valor: "",
    dataEntrada: hojeLocal(),
  })

  useEffect(() => {
    fetchCircuitos()
  }, [])

  useEffect(() => {
    fetchSaques()
    fetchEntradas()
    fetchResumo()

    if (circuitoSelecionado) {
      setFormEntrada((atual) => ({ ...atual, circuitoId: circuitoSelecionado }))
    }
  }, [circuitoSelecionado])

  const tokenHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  })

  const fetchCircuitos = async () => {
    try {
      const res = await axios.get("https://api.ticketdovaqueiro.com/api/circuitos")
      setCircuitos(res.data)
    } catch (err) {
      console.error("Erro ao carregar circuitos:", err)
    }
  }

  const fetchSaques = async () => {
    try {
      setLoading(true)
      const params = { circuitoId: circuitoSelecionado || null }
      const res = await axios.get(
        "https://api.ticketdovaqueiro.com/api/circuitos/admin/saques",
        { headers: tokenHeaders(), params }
      )
      setSaques(res.data)
    } catch (err) {
      console.error("Erro ao carregar saques dos circuitos:", err)
    } finally {
      setLoading(false)
    }
  }

  const fetchEntradas = async () => {
    try {
      const params = { circuitoId: circuitoSelecionado || null }
      const res = await axios.get(
        "https://api.ticketdovaqueiro.com/api/circuitos/admin/entradas",
        { headers: tokenHeaders(), params }
      )
      setEntradas(res.data)
    } catch (err) {
      console.error("Erro ao carregar entradas dos circuitos:", err)
    }
  }

  const fetchResumo = async () => {
    try {
      const params = { circuitoId: circuitoSelecionado || null }
      const res = await axios.get(
        "https://api.ticketdovaqueiro.com/api/circuitos/admin/resumo",
        { headers: tokenHeaders(), params }
      )
      setResumo(res.data)
    } catch (err) {
      console.error("Erro ao carregar resumo financeiro:", err)
    }
  }

  const adicionarEntrada = async (e) => {
    e.preventDefault()

    const valor = Number(String(formEntrada.valor).replace(",", "."))
    if (!formEntrada.circuitoId) {
      setMensagem("Selecione o circuito que receberá a entrada.")
      setTipoMensagem("error")
      return
    }
    if (!valor || valor <= 0) {
      setMensagem("Informe um valor de entrada válido.")
      setTipoMensagem("error")
      return
    }

    if (
      !window.confirm(
        `Confirmar entrada de R$ ${valor.toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
        })} no financeiro deste circuito?`
      )
    ) return

    try {
      setSalvandoEntrada(true)
      const res = await axios.post(
        "https://api.ticketdovaqueiro.com/api/circuitos/admin/entradas",
        {
          circuitoId: formEntrada.circuitoId,
          tipo: formEntrada.tipo,
          descricao: formEntrada.descricao,
          valor,
          dataEntrada: formEntrada.dataEntrada
            ? `${formEntrada.dataEntrada}T12:00:00`
            : undefined,
        },
        { headers: tokenHeaders() }
      )

      setMensagem(res.data?.mensagem || "Entrada adicionada com sucesso!")
      setTipoMensagem("success")
      setFormEntrada((atual) => ({
        ...atual,
        tipo: "Patrocínio",
        descricao: "",
        valor: "",
        dataEntrada: hojeLocal(),
      }))
      await Promise.all([fetchEntradas(), fetchResumo()])
    } catch (err) {
      setMensagem(err.response?.data?.error || "Erro ao adicionar entrada.")
      setTipoMensagem("error")
    } finally {
      setSalvandoEntrada(false)
    }
  }

  const cancelarEntrada = async (id) => {
    if (
      !window.confirm(
        "Cancelar esta entrada? O valor deixará de compor a arrecadação e o saldo disponível do circuito."
      )
    ) return

    try {
      const res = await axios.patch(
        `https://api.ticketdovaqueiro.com/api/circuitos/admin/entradas/${id}/cancelar`,
        {},
        { headers: tokenHeaders() }
      )
      setMensagem(res.data?.mensagem || "Entrada cancelada.")
      setTipoMensagem("success")
      await Promise.all([fetchEntradas(), fetchResumo()])
    } catch (err) {
      setMensagem(err.response?.data?.error || "Erro ao cancelar entrada.")
      setTipoMensagem("error")
    }
  }

  const aprovarSaque = async (id) => {
    if (!window.confirm("Confirmar a transferência e aprovar este saque?")) return
    try {
      await axios.patch(
        `https://api.ticketdovaqueiro.com/api/circuitos/admin/saques/${id}/aprovar`,
        {},
        { headers: tokenHeaders() }
      )
      setMensagem("Saque aprovado com sucesso!")
      setTipoMensagem("success")
      await Promise.all([fetchSaques(), fetchResumo()])
    } catch (err) {
      setMensagem(err.response?.data?.mensagem || "Erro ao aprovar saque.")
      setTipoMensagem("error")
    }
  }

  const cancelarSaque = async (id, statusAtual) => {
    const alertaMsg =
      statusAtual === "aprovado"
        ? "ATENÇÃO: Este saque JÁ ESTÁ APROVADO! Deseja realmente ESTORNAR/CANCELAR? O valor voltará para o saldo disponível do circuito."
        : "Deseja realmente RECUSAR este pedido de saque? O valor voltará para o saldo do circuito."

    if (!window.confirm(alertaMsg)) return

    try {
      await axios.patch(
        `https://api.ticketdovaqueiro.com/api/circuitos/admin/saques/${id}/cancelar`,
        {},
        { headers: tokenHeaders() }
      )
      setMensagem("Saque cancelado com sucesso!")
      setTipoMensagem("success")
      await Promise.all([fetchSaques(), fetchResumo()])
    } catch (err) {
      setMensagem("Erro ao cancelar saque.")
      setTipoMensagem("error")
    }
  }

  const deletarSaque = async (id) => {
    if (
      !window.confirm(
        "Deseja APAGAR este saque cancelado do histórico permanentemente? Essa ação não pode ser desfeita."
      )
    ) return

    try {
      await axios.delete(
        `https://api.ticketdovaqueiro.com/api/circuitos/admin/saques/${id}`,
        { headers: tokenHeaders() }
      )
      setMensagem("Registro excluído do histórico com sucesso!")
      setTipoMensagem("success")
      fetchSaques()
    } catch (err) {
      setMensagem(err.response?.data?.error || "Erro ao excluir registro.")
      setTipoMensagem("error")
    }
  }

  const fC = (valor) =>
    `R$ ${Number(valor || 0).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`

  return (
    <div className="financeiro-admin-container">
      <h2>
        <FaMoneyBillWave /> Financeiro dos Circuitos
      </h2>

      <div className="filtro-evento">
        <label>
          <FaFilter /> Filtrar por Circuito:
        </label>
        <select
          value={circuitoSelecionado}
          onChange={(e) => setCircuitoSelecionado(e.target.value)}
        >
          <option value="">Todos os Circuitos</option>
          {circuitos.map((circuito) => (
            <option key={circuito._id} value={circuito._id}>
              {circuito.nome}
            </option>
          ))}
        </select>
      </div>

      {mensagem && <div className={`mensagem ${tipoMensagem}`}>{mensagem}</div>}

      <div className="grid-resumo-admin">
        <div className="grid-item-admin">
          <h4>Saldo Disponível Total</h4>
          <p style={{ color: "#10b981" }}>{fC(resumo.totalDisponivel)}</p>
        </div>
        <div className="grid-item-admin">
          <h4>Arrecadado em Vendas</h4>
          <p style={{ color: "#38bdf8" }}>{fC(resumo.totalArrecadadoVendas)}</p>
        </div>
        <div className="grid-item-admin">
          <h4>Entradas Manuais</h4>
          <p style={{ color: "#10b981" }}>{fC(resumo.totalEntradas)}</p>
        </div>
        <div className="grid-item-admin">
          <h4>Arrecadação Geral</h4>
          <p style={{ color: "#22c55e" }}>{fC(resumo.totalArrecadadoGeral)}</p>
        </div>
        <div className="grid-item-admin">
          <h4>Saques Aprovados</h4>
          <p style={{ color: "#3b82f6" }}>{fC(resumo.totalSaquesAprovados)}</p>
        </div>
        <div className="grid-item-admin">
          <h4>Saques Pendentes</h4>
          <p style={{ color: "#f59e0b" }}>{fC(resumo.totalSaquesPendentes)}</p>
        </div>
      </div>

      <div className="tabela-financeira" style={{ marginBottom: 24 }}>
        <h2>
          <FaPlusCircle /> Adicionar Entrada
        </h2>
        <p style={{ color: "rgba(255,255,255,.6)", marginTop: -8, marginBottom: 20 }}>
          Use para patrocínios, aportes ou outros valores recebidos fora das inscrições.
          A entrada ativa soma na arrecadação e no saldo disponível.
        </p>

        <form
          onSubmit={adicionarEntrada}
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 14,
            alignItems: "end",
          }}
        >
          <div>
            <label style={{ display: "block", marginBottom: 6 }}>Circuito</label>
            <select
              required
              value={formEntrada.circuitoId}
              onChange={(e) =>
                setFormEntrada({ ...formEntrada, circuitoId: e.target.value })
              }
              style={{ width: "100%" }}
            >
              <option value="">Selecione...</option>
              {circuitos.map((circuito) => (
                <option key={circuito._id} value={circuito._id}>
                  {circuito.nome}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: "block", marginBottom: 6 }}>Tipo</label>
            <select
              value={formEntrada.tipo}
              onChange={(e) =>
                setFormEntrada({ ...formEntrada, tipo: e.target.value })
              }
              style={{ width: "100%" }}
            >
              <option value="Patrocínio">Patrocínio</option>
              <option value="Aporte">Aporte</option>
              <option value="Venda externa">Venda externa</option>
              <option value="Premiação / apoio">Premiação / apoio</option>
              <option value="Outro">Outro</option>
            </select>
          </div>

          <div>
            <label style={{ display: "block", marginBottom: 6 }}>Data</label>
            <input
              type="date"
              required
              value={formEntrada.dataEntrada}
              onChange={(e) =>
                setFormEntrada({ ...formEntrada, dataEntrada: e.target.value })
              }
              style={{ width: "100%" }}
            />
          </div>

          <div>
            <label style={{ display: "block", marginBottom: 6 }}>Valor (R$)</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              required
              value={formEntrada.valor}
              onChange={(e) =>
                setFormEntrada({ ...formEntrada, valor: e.target.value })
              }
              placeholder="Ex.: 1500,00"
              style={{ width: "100%" }}
            />
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <label style={{ display: "block", marginBottom: 6 }}>Descrição</label>
            <input
              type="text"
              maxLength={300}
              required
              value={formEntrada.descricao}
              onChange={(e) =>
                setFormEntrada({ ...formEntrada, descricao: e.target.value })
              }
              placeholder="Ex.: Patrocínio da Empresa X - Etapa Cajazeiras"
              style={{ width: "100%" }}
            />
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <button
              type="submit"
              disabled={salvandoEntrada}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: 0,
                borderRadius: 10,
                padding: "11px 18px",
                fontWeight: 800,
                cursor: salvandoEntrada ? "wait" : "pointer",
                background: "#10b981",
                color: "#fff",
                opacity: salvandoEntrada ? 0.7 : 1,
              }}
            >
              <FaHandHoldingUsd />
              {salvandoEntrada ? "Salvando..." : "Adicionar Entrada"}
            </button>
          </div>
        </form>
      </div>

      <div className="tabela-financeira" style={{ marginBottom: 24 }}>
        <h2>
          <FaHandHoldingUsd /> Histórico de Entradas
        </h2>

        {entradas.length === 0 ? (
          <div className="empty-state">
            <p>Nenhuma entrada manual encontrada.</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="extrato-tabela">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Circuito</th>
                  <th>Tipo</th>
                  <th>Descrição</th>
                  <th>Valor</th>
                  <th>Status</th>
                  <th>Ação</th>
                </tr>
              </thead>
              <tbody>
                {entradas.map((entrada) => (
                  <tr
                    key={entrada._id}
                    style={{ opacity: entrada.status === "cancelado" ? 0.55 : 1 }}
                  >
                    <td>
                      {new Date(entrada.dataEntrada || entrada.createdAt).toLocaleDateString("pt-BR")}
                    </td>
                    <td>
                      <strong>{entrada.circuitoId?.nome || "Circuito Removido"}</strong>
                      {entrada.criadoPor?.nomeCompleto && (
                        <>
                          <br />
                          <small style={{ color: "rgba(255,255,255,.5)" }}>
                            Lançado por: {entrada.criadoPor.nomeCompleto}
                          </small>
                        </>
                      )}
                    </td>
                    <td>{entrada.tipo || "Outro"}</td>
                    <td>{entrada.descricao}</td>
                    <td
                      style={{
                        fontWeight: 800,
                        color: entrada.status === "cancelado" ? "#888" : "#10b981",
                        textDecoration:
                          entrada.status === "cancelado" ? "line-through" : "none",
                      }}
                    >
                      {fC(entrada.valor)}
                    </td>
                    <td>
                      <span
                        className={`status ${
                          entrada.status === "ativo" ? "aprovado" : "cancelado"
                        }`}
                      >
                        {entrada.status === "ativo" ? "Ativa" : "Cancelada"}
                      </span>
                    </td>
                    <td>
                      {entrada.status === "ativo" && (
                        <FaTimesCircle
                          className="icone-acao vermelho"
                          onClick={() => cancelarEntrada(entrada._id)}
                          title="Cancelar / estornar entrada"
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="tabela-financeira">
        <h2>
          <FaExchangeAlt /> Solicitações de Saque (Circuitos)
        </h2>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Carregando saques...</p>
          </div>
        ) : saques.length === 0 ? (
          <div className="empty-state">
            <p>Nenhuma solicitação de saque de circuito encontrada.</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="extrato-tabela">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Circuito</th>
                  <th>Titular da Conta</th>
                  <th>Chave PIX</th>
                  <th>Valor</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {saques.map((saque) => (
                  <tr
                    key={saque._id}
                    style={{ opacity: saque.status === "cancelado" ? 0.6 : 1 }}
                  >
                    <td>
                      {new Date(saque.dataSolicitacao).toLocaleDateString("pt-BR")}
                    </td>
                    <td>
                      <strong>{saque.circuitoId?.nome || "Circuito Removido"}</strong>
                      <br />
                      <small style={{ color: "rgba(255,255,255,0.5)" }}>
                        Solicitante: {saque.donoId?.nomeCompleto?.split(" ")[0]}
                      </small>
                    </td>
                    <td>
                      {saque.titular}
                      <br />
                      <small style={{ color: "rgba(255,255,255,0.5)" }}>
                        CPF: {saque.cpfTitular}
                      </small>
                    </td>
                    <td>
                      <span
                        style={{
                          fontSize: 10,
                          background: "rgba(255,255,255,0.1)",
                          padding: "2px 6px",
                          borderRadius: 4,
                          textTransform: "uppercase",
                          marginRight: 6,
                        }}
                      >
                        {saque.chavePix.tipo}
                      </span>
                      {saque.chavePix.valor}
                    </td>
                    <td
                      style={{
                        fontWeight: "bold",
                        color: saque.status === "cancelado" ? "#888" : "#10b981",
                        fontSize: "16px",
                        textDecoration:
                          saque.status === "cancelado" ? "line-through" : "none",
                      }}
                    >
                      {fC(saque.valor)}
                    </td>
                    <td>
                      <span className={`status ${saque.status}`}>
                        {saque.status === "aprovado" && "Transferido"}
                        {saque.status === "pendente" && (
                          <>
                            <FaHourglassHalf /> Pendente
                          </>
                        )}
                        {saque.status === "cancelado" && "Cancelado"}
                      </span>
                    </td>
                    <td
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {saque.status === "pendente" && (
                        <FaCheckCircle
                          className="icone-acao verde"
                          onClick={() => aprovarSaque(saque._id)}
                          title="Aprovar transferência"
                        />
                      )}

                      {(saque.status === "pendente" ||
                        saque.status === "aprovado") && (
                        <FaTimesCircle
                          className="icone-acao vermelho"
                          onClick={() => cancelarSaque(saque._id, saque.status)}
                          title={
                            saque.status === "aprovado"
                              ? "Estornar/Cancelar Saque Aprovado"
                              : "Cancelar/Recusar"
                          }
                        />
                      )}

                      {saque.status === "cancelado" && (
                        <FaTrash
                          className="icone-acao vermelho"
                          style={{ color: "#dc2626", cursor: "pointer" }}
                          onClick={() => deletarSaque(saque._id)}
                          title="Apagar registro do histórico"
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default FinanceiroCircuitoAdmin
